import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarDays, Loader2, Send } from 'lucide-react';
import { track } from '@/lib/analytics';
import { supabase } from '@/lib/supabase';
import { SupportMessage } from '@/types';
import {
  SupportMessageRow,
  SupportThread,
  fetchLatestThread,
  mapSupportMessageRow,
  markThreadReadByUser,
  sendUserMessage,
} from '@/services/supportService';
import { Button } from '@/components/ui/button';

const CAL_LINK = 'https://calendly.com/estage-vision/30min';

interface SupportChatPanelProps {
  /** Panel wurde geöffnet und Admin-Antworten als gelesen markiert → Badge zurücksetzen. */
  onUnreadCleared: () => void;
}

export const SupportChatPanel: React.FC<SupportChatPanelProps> = ({ onUnreadCleared }) => {
  const [thread, setThread] = useState<SupportThread | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetchLatestThread()
      .then((t) => {
        if (cancelled) return;
        setThread(t);
        if (t && t.messages.some((m) => m.senderRole === 'admin' && !m.readByUser)) {
          void markThreadReadByUser(t.ticket.id);
          onUnreadCleared();
        }
      })
      .catch(() => { if (!cancelled) setError('Nachrichten konnten nicht geladen werden.'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [onUnreadCleared]);

  // Realtime: Admin-Antworten landen live im offenen Panel (Publication: Migration 122).
  // Eigene (User-)Nachrichten sind bereits optimistisch im State → nur Admin-Events verarbeiten.
  useEffect(() => {
    const ticketId = thread?.ticket.id;
    if (!ticketId) return;
    const channel = supabase
      .channel(`support-thread-${ticketId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'support_messages', filter: `ticket_id=eq.${ticketId}` },
        (payload) => {
          const row = payload.new as SupportMessageRow;
          if (row.sender_role !== 'admin') return;
          setThread((prev) => {
            if (!prev || prev.ticket.id !== row.ticket_id) return prev;
            if (prev.messages.some((m) => m.id === row.id)) return prev;
            return { ...prev, messages: [...prev.messages, mapSupportMessageRow(row)] };
          });
          void markThreadReadByUser(ticketId); // Panel ist offen → direkt als gelesen markieren
          onUnreadCleared();
        },
      )
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [thread?.ticket.id, onUnreadCleared]);

  // Ans Ende scrollen, wenn Nachrichten da sind / dazukommen
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [thread?.messages.length, isLoading]);

  const handleSend = useCallback(async () => {
    const body = draft.trim();
    if (!body || isSending) return;
    setIsSending(true);
    setError(null);
    try {
      // Geschlossenes Ticket → neue Nachricht eröffnet eine neue Anfrage
      const activeTicketId = thread && thread.ticket.status !== 'closed' ? thread.ticket.id : null;
      const { ticketId, message, createdTicket } = await sendUserMessage(activeTicketId, body);
      if (createdTicket) track('Support Ticket Created', { ticket_id: ticketId });
      track('Support Message Sent', { ticket_id: ticketId, sender_role: 'user' });
      setDraft('');
      setThread((prev) =>
        prev && prev.ticket.id === ticketId
          ? { ...prev, messages: [...prev.messages, message] }
          : {
              ticket: {
                id: ticketId,
                userId: '',
                status: 'open',
                createdAt: message.createdAt,
                updatedAt: message.createdAt,
              },
              messages: [message],
            },
      );
    } catch {
      setError('Senden fehlgeschlagen. Bitte versuche es erneut.'); // Draft bleibt erhalten
    } finally {
      setIsSending(false);
    }
  }, [draft, isSending, thread]);

  const messages: SupportMessage[] = thread?.messages ?? [];

  return (
    <div className="flex flex-col h-96">
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <p className="text-sm font-medium text-foreground">Wie können wir helfen?</p>
            <p className="text-xs text-muted-foreground mt-1">
              Schreib uns deine Frage — wir melden uns so schnell wie möglich.
            </p>
          </div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className={`flex ${m.senderRole === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                  m.senderRole === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-sm'
                    : 'bg-muted text-foreground rounded-bl-sm'
                }`}
              >
                {m.body}
                <div className={`text-[10px] mt-1 ${m.senderRole === 'user' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                  {new Date(m.createdAt).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {error && <p className="text-xs text-destructive mt-2">{error}</p>}

      <div className="mt-3 flex items-end gap-2">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend(); }
          }}
          placeholder="Deine Nachricht…"
          rows={2}
          className="flex-1 resize-none rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
        />
        <Button size="sm" onClick={() => void handleSend()} disabled={!draft.trim() || isSending} aria-label="Senden">
          {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>

      <div className="mt-3 border-t border-border pt-2 space-y-1">
        <p className="text-[11px] text-muted-foreground">Wir antworten in der Regel innerhalb eines Werktags.</p>
        <a
          href={CAL_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
        >
          <CalendarDays className="h-3 w-3" /> Lieber persönlich? Beratungstermin buchen
        </a>
      </div>
    </div>
  );
};
